// Role-wise attendance policy — judged on market (call) hours.
// Total market hours = last call end − first call start, per day. Backend: attendance_policy.py
import { useCallback, useEffect, useState } from "react";
import { Loader2, Save, Timer } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import PageHeader from "@/components/PageHeader";

const FIELDS = ["enabled", "present_min_hours", "half_day_min_hours", "on_time_before", "half_day_if_start_after"];

export default function AttendancePolicy() {
  const [rows, setRows] = useState([]);
  const [orig, setOrig] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingRole, setSavingRole] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/attendance-policies");
      setRows(r.data.items);
      setOrig(Object.fromEntries(r.data.items.map((x) => [x.role_code, x])));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = (role, k, v) => setRows((rs) => rs.map((r) => (r.role_code === role ? { ...r, [k]: v } : r)));
  const dirty = (r) => FIELDS.some((f) => r[f] !== orig[r.role_code]?.[f]);

  const save = async (r) => {
    setSavingRole(r.role_code);
    try {
      await api.put(`/attendance-policies/${r.role_code}`, {
        enabled: !!r.enabled,
        present_min_hours: Number(r.present_min_hours),
        half_day_min_hours: Number(r.half_day_min_hours),
        on_time_before: r.on_time_before,
        half_day_if_start_after: r.half_day_if_start_after,
      });
      toast.success(`${r.role_name}: policy ${r.enabled ? "saved and active" : "saved (turned off)"}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingRole(null); }
  };

  return (
    <div className="p-4 space-y-4" data-testid="attendance-policy-page">
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Masters" }, { label: "Attendance Policy" }]}
        title="Attendance Policy"
        subtitle="Role-wise rules based on market hours: last call end − first call start, each day. Roles with the policy off keep the normal punch-based attendance."
      />
      <Card className="border-neutral-200 rounded-2xl bg-neutral-50/60">
        <CardContent className="p-3 text-xs text-neutral-600 space-y-1">
          <div className="font-semibold text-neutral-800 flex items-center gap-1.5"><Timer className="h-4 w-4 text-[#6D2C91]" /> How a day is decided</div>
          <div>Market hours ≥ <b>Present hours</b> → Present · between <b>Half-day hours</b> and Present hours → Half day · below Half-day hours (or no completed calls) → Absent.</div>
          <div>First call after <b>Half day if market starts after</b> → the day is capped at Half day, even with full hours.</div>
          <div>First call after <b>On time before</b> is counted as a late day (shown to the user and in the Attendance Report).</div>
          <div>Holidays, weekly offs, approved leave and tours are not affected unless the user actually worked.</div>
        </CardContent>
      </Card>

      {loading ? <Loader2 className="h-5 w-5 animate-spin mx-auto text-neutral-400" /> : (
        <div className="space-y-2">
          {rows.map((r) => (
            <Card key={r.role_code} className="border-neutral-200 rounded-2xl" data-testid={`ap-row-${r.role_code}`}>
              <CardContent className="p-3 flex flex-wrap items-end gap-3">
                <div className="min-w-[150px] flex-1">
                  <div className="text-sm font-bold text-neutral-900">{r.role_name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-400">{r.role_code}</div>
                  {r.updated_by_name && (
                    <div className="text-[10px] text-neutral-400">Saved by {r.updated_by_name}</div>
                  )}
                </div>
                <label className="flex items-center gap-2 text-xs text-neutral-600">
                  <Switch checked={!!r.enabled} onCheckedChange={(v) => patch(r.role_code, "enabled", v)}
                          data-testid={`ap-enabled-${r.role_code}`} />
                  {r.enabled ? "Active" : "Off"}
                </label>
                <Num label="Present ≥ (hours)" value={r.present_min_hours} onChange={(v) => patch(r.role_code, "present_min_hours", v)} testid={`ap-present-${r.role_code}`} />
                <Num label="Half day ≥ (hours)" value={r.half_day_min_hours} onChange={(v) => patch(r.role_code, "half_day_min_hours", v)} testid={`ap-half-${r.role_code}`} />
                <TimeIn label="On time before" value={r.on_time_before} onChange={(v) => patch(r.role_code, "on_time_before", v)} testid={`ap-ontime-${r.role_code}`} />
                <TimeIn label="Half day if market starts after" value={r.half_day_if_start_after} onChange={(v) => patch(r.role_code, "half_day_if_start_after", v)} testid={`ap-start-${r.role_code}`} />
                <Button size="sm" onClick={() => save(r)} disabled={!dirty(r) || savingRole === r.role_code}
                        className="tulsi-gradient text-white" data-testid={`ap-save-${r.role_code}`}>
                  {savingRole === r.role_code ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                  Save
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function Num({ label, value, onChange, testid }) {
  return (
    <div className="w-[120px]">
      <div className="text-[10px] text-neutral-500 mb-0.5">{label}</div>
      <Input type="number" min="0.5" max="24" step="0.25" value={value}
             onChange={(e) => onChange(e.target.value)} className="h-9" data-testid={testid} />
    </div>
  );
}
function TimeIn({ label, value, onChange, testid }) {
  return (
    <div className="w-[150px]">
      <div className="text-[10px] text-neutral-500 mb-0.5">{label}</div>
      <Input type="time" value={value} onChange={(e) => onChange(e.target.value)} className="h-9" data-testid={testid} />
    </div>
  );
}
